import {
  listAccessibleOrganizations,
  type AccessibleOrganization,
} from "@/lib/organizations";

export const CURRENT_ORG_COOKIE = "current_org_id";
const CURRENT_ORG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function parseCookieHeader(cookieHeader?: string): Map<string, string> {
  const cookies = new Map<string, string>();

  if (!cookieHeader) {
    return cookies;
  }

  for (const rawPart of cookieHeader.split(";")) {
    const part = rawPart.trim();
    if (!part) {
      continue;
    }

    const separatorIndex = part.indexOf("=");
    if (separatorIndex === -1) {
      continue;
    }

    const key = decodeURIComponent(part.slice(0, separatorIndex).trim());
    const value = decodeURIComponent(part.slice(separatorIndex + 1).trim());
    cookies.set(key, value);
  }

  return cookies;
}

function serializeCookieHeader(cookies: Map<string, string>): string {
  return Array.from(cookies.entries())
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("; ");
}

export function getCurrentOrganizationId(cookieHeader?: string): string | null {
  return parseCookieHeader(cookieHeader).get(CURRENT_ORG_COOKIE) ?? null;
}

export function withCurrentOrganizationCookie(cookieHeader: string | undefined, orgId: string): string {
  const cookies = parseCookieHeader(cookieHeader);
  cookies.set(CURRENT_ORG_COOKIE, orgId);
  return serializeCookieHeader(cookies);
}

export function pickCurrentOrganization(
  organizations: AccessibleOrganization[],
  preferredOrgId?: string | null
): AccessibleOrganization | null {
  if (organizations.length === 0) {
    return null;
  }

  if (preferredOrgId) {
    const matchedOrganization = organizations.find((organization) => organization.id === preferredOrgId);
    if (matchedOrganization) {
      return matchedOrganization;
    }
  }

  return organizations[0];
}

export async function resolveTenantContext(cookieHeader?: string): Promise<{
  organizations: AccessibleOrganization[];
  currentOrganization: AccessibleOrganization | null;
}> {
  const organizations = await listAccessibleOrganizations(cookieHeader);
  const currentOrganization = pickCurrentOrganization(
    organizations,
    getCurrentOrganizationId(cookieHeader)
  );

  return {
    organizations,
    currentOrganization,
  };
}

export function setCurrentOrganizationCookie(orgId: string): void {
  document.cookie = [
    `${CURRENT_ORG_COOKIE}=${encodeURIComponent(orgId)}`,
    "path=/",
    `max-age=${CURRENT_ORG_COOKIE_MAX_AGE}`,
    "SameSite=Lax",
  ].join("; ");
}
